import { keys, sortBy } from "lodash";
import { Button } from "PFComponents/button";
import ButtonGroup from "PFComponents/button_group/button_group";
import { DataTableColumn } from "PFComponents/data_table/data_table";
import Toggle from "PFComponents/toggle/toggle";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { CrossGroupPermission, CustomType } from "PFTypes";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  setCrossGroupActivityType,
  setCrossGroupCustomType,
  setCrossGroupProfileField
} from "../permissions_form_data";
import PermissionsGroupATEdit from "../permissions_group_at_edit";
import css from "../permissions_group_edit_page.module.scss";
import { FormData } from "../permissions_group_edit_page.types";
import PermissionsGroupItemsEdit from "../permissions_group_items_edit";
import PermissionsGroupProfileFieldsEdit from "../permissions_group_profile_fields_edit";
import { PermissionGroupSketch } from "./use_functionality_permissions";

type Tab = {
  expandedPGId: number | "self";
  sectionType: "profileFields" | "at" | "ct";
};

type UseCrossGroupPermissions = {
  permissionsGroup: PermissionGroupSketch;
  formData: FormData;
  permissionsGroupsList: PermissionGroupSketch[];
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  customTypes: CustomType[];
};

type UseCrossGroupPermissionsReturn = {
  columns: DataTableColumn[];
  renderFocusRow: (group: PermissionGroupSketch) => ReactNode;
};

export const useCrossGroupPermissions = ({
  permissionsGroup,
  formData,
  setFormData,
  permissionsGroupsList,
  customTypes
}: UseCrossGroupPermissions): UseCrossGroupPermissionsReturn => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("admin", { keyPrefix: "permissionsGroups" });
  const activityTypes = currentProfile.templates;
  const profileFields = currentProfile.permissions_settings?.default_profile_fields_permissions;

  const [ctPermissionCache, setCtPermissionCache] = useState<Record<number, CrossGroupPermission>>({}); // persist ct permissions when toggling cross-group permissions
  const [currentTab, setCurrentTab] = useState<Tab | undefined>(undefined);
  const crossGroupPermissions = formData.cross_group_permissions || {};
  const columns = [
    {
      narrow: true,
      renderHeader: () => (
        <Button
          kind="secondary"
          small
          onClick={() => {
            let perms = formData.cross_group_permissions;
            const totalChecked = keys(perms).length;
            const totalItems = permissionsGroupsList.length;

            if (totalChecked < totalItems) {
              permissionsGroupsList.forEach((group) => {
                perms[group.id] = perms[group.id] || ctPermissionCache[group.id] || {};
              });
            } else {
              const newCtPermissionCache = { ...ctPermissionCache };
              permissionsGroupsList.forEach((group) => {
                const gid = group.id;
                newCtPermissionCache[gid] = perms[gid] || ctPermissionCache[gid];
              });
              setCtPermissionCache(newCtPermissionCache);
              perms = {};
            }
            setFormData({ ...formData, cross_group_permissions: perms });
          }}
        >
          {t("permissions.toggleAll")}
        </Button>
      ),
      renderCell: (group) => (
        <Toggle
          checked={!!formData.cross_group_permissions[group.id]}
          onChange={(val) => {
            const perms = formData.cross_group_permissions;

            if (val) {
              perms[group.id] = ctPermissionCache[group.id] || {
                custom_types_access_levels: {}
              };
            } else {
              setCtPermissionCache({ ...ctPermissionCache, [group.id]: perms[group.id] });
              delete perms[group.id];
            }

            setFormData({ ...formData, cross_group_permissions: perms });
          }}
        />
      )
    },
    {
      narrow: true,
      renderCell(group) {
        return (group.id === permissionsGroup.id || group.id === "self") && <small>current</small>;
      }
    },
    { headerName: t("permissions.headerName"), cellKey: "name" },
    {
      narrow: false,
      renderCell: (group) => {
        if (!crossGroupPermissions[group.id]) {
          // to edit the ct access you need the access to the pg in the first place
          return false;
        }

        return (
          <ButtonGroup autoWidth className={css.buttonGroup}>
            <Button
              kind="primary"
              small
              onClick={() => {
                const isOpen = currentTab?.sectionType === "ct" && currentTab?.expandedPGId === group.id;
                if (isOpen) {
                  setCurrentTab(undefined);
                } else {
                  setCurrentTab({ expandedPGId: group.id, sectionType: "ct" });
                }
              }}
            >
              {t("permissions.crossGroupPermissions.customTypes")}
            </Button>
            <Button
              kind="primary"
              small
              onClick={() => {
                const isOpen = currentTab?.sectionType === "at" && currentTab?.expandedPGId === group.id;
                if (isOpen) {
                  setCurrentTab(undefined);
                } else {
                  setCurrentTab({ expandedPGId: group.id, sectionType: "at" });
                }
              }}
            >
              {t("permissions.crossGroupPermissions.activityTypes")}
            </Button>
            <Button
              kind="primary"
              small
              onClick={() => {
                const isOpen =
                  currentTab?.sectionType === "profileFields" && currentTab?.expandedPGId === group.id;
                if (isOpen) {
                  setCurrentTab(undefined);
                } else {
                  setCurrentTab({
                    expandedPGId: group.id,
                    sectionType: "profileFields"
                  });
                }
              }}
            >
              {t("permissions.crossGroupPermissions.profileFields")}
            </Button>
          </ButtonGroup>
        );
      }
    }
  ];

  const renderFocusRow = (group) => {
    if (!crossGroupPermissions[group.id]) {
      return false;
    }

    if (group.id === currentTab?.expandedPGId || (currentTab?.expandedPGId === "self" && !group.id)) {
      if (currentTab?.sectionType === "ct") {
        return (
          <PermissionsGroupItemsEdit
            dataTableQAId="PermissionsGroupCTEdit"
            isRWOnly={(ct) => {
              const isCurrentPG = !!(group.id === formData.id || group.id === "self");
              const isMandatory = !!ct.mandatory;
              return isCurrentPG && isMandatory;
            }}
            items={sortBy(customTypes, "display_as")}
            idToPermLevel={(crossGroupPermissions[group.id] || {}).custom_types_access_levels || {}}
            handleChange={(ctId, level) =>
              setFormData((prevFormData) => ({
                ...prevFormData,
                ...setCrossGroupCustomType(prevFormData, group.id, ctId, level)
              }))
            }
            getItemName={(ct) => ct.name}
            getDisplayAs={(ct) => ct.display_as}
          />
        );
      } else if (currentTab?.sectionType === "profileFields") {
        return (
          <PermissionsGroupProfileFieldsEdit
            isCurrentPG={!!(group.id === formData.id || group.id === "self")}
            profileFields={profileFields}
            profileFieldToPermLevel={
              (crossGroupPermissions[group.id] || {}).profile_fields_access_levels || {}
            }
            handleChange={(fieldId, level) =>
              setFormData((prevFormData) => ({
                ...prevFormData,
                ...setCrossGroupProfileField(prevFormData, group.id, fieldId, level)
              }))
            }
          />
        );
      } else {
        return (
          <PermissionsGroupATEdit
            isCurrentPG={!!(group.id === formData.id || group.id === "self")}
            activityTypes={sortBy(activityTypes, ["name"])}
            atIdToPermLevel={(crossGroupPermissions[group.id] || {}).activities_access_levels || {}}
            handleChange={(atId, level) =>
              setFormData((prevFormData) => ({
                ...prevFormData,
                ...setCrossGroupActivityType(prevFormData, group.id, atId, level)
              }))
            }
          />
        );
      }
    }
  };

  return {
    columns,
    renderFocusRow
  };
};
